import { useMemo, useState } from "react";

import { CHAMPION_ROUND, api, type Heat, type Member, type Tournament } from "../api";
import { useAction } from "../useLoad";
import { MemberAvatar } from "../avatar";
import { PickedBy, backersOf } from "./picked";

interface Props {
  teamId: string;
  tournament: Tournament;
  members: Member[];
  mySub: string;
  names: Map<string, string>;
  onChanged: () => void;
  onError: (message: string) => void;
}

type RoundState = "waiting" | "open" | "revealed";

/**
 * Where a round is in the pick lifecycle. Mirrors `scoring.round_state` on the server.
 *
 * Derived from the bracket rather than asked for, because it already is derivable and a client that
 * asked would be able to disagree with the server about whether it may still pick. Getting this wrong
 * in the lenient direction shows a form that the server then refuses; getting it wrong the other way
 * hides a form that would have worked.
 *
 * Note that `open` means "pickable **and** visible" per requirement 9.4.1 — this drives the form only;
 * what may be *seen* is decided by `scoring.visible_picks` on the server and arrives already filtered.
 */
export function roundState(tournament: Tournament, round: number): RoundState {
  if (round === CHAMPION_ROUND) return roundState(tournament, 1);
  const heats = tournament.heats.filter((heat) => heat.round === round);
  if (heats.length === 0) return "waiting";
  if (heats.some((heat) => heat.state === "complete")) return "revealed";
  if (heats.some((heat) => heat.participants.length !== tournament.width)) return "waiting";
  return "open";
}

/**
 * Predictions, from the phone. Tasks 11.1 through 11.4 and 11.7.
 *
 * Requirement 9.1 — you pick **one round ahead of known results**, so there is only ever one round to
 * pick and this screen shows that one. No bracket-wide form, no cascading dependencies, and nothing to
 * pick for a race whose entrants are still being decided.
 *
 * Requirement 9.2 — picks are **unordered**. You tap which vehicles advance, not in what order. The
 * finishing order is still recorded for stats and seeding; scoring order too would double the mental
 * load for a child following the leaderboard, which is the entire reason this screen is taps and not a
 * ranked list.
 */
export function Picks({
  teamId,
  tournament,
  members,
  mySub,
  names,
  onChanged,
  onError,
}: Props) {
  const { busy, run } = useAction(onError);
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [champion, setChampion] = useState<string | null>(null);

  // The one round that can be picked, if any. Lowest first, so a family who has not picked round two
  // while round three is somehow open would be shown round two.
  const openRound = useMemo(() => {
    for (let round = 1; round <= tournament.roundCount; round += 1) {
      if (roundState(tournament, round) === "open") return round;
    }
    return null;
  }, [tournament]);

  const perHeat = Math.max(1, tournament.width / 2);
  const myPicks = useMemo(
    () => new Map(tournament.picks.filter((pick) => pick.sub === mySub).map((p) => [p.round, p])),
    [tournament.picks, mySub],
  );

  const championState = roundState(tournament, CHAMPION_ROUND);
  const myChampionPick = myPicks.get(CHAMPION_ROUND)?.champion ?? null;

  const heats = openRound
    ? tournament.heats.filter((heat) => heat.round === openRound)
    : [];
  const selected = draft ?? new Set(myPicks.get(openRound ?? -1)?.advancers ?? []);

  const perHeatCount = (heat: Heat) =>
    heat.participants.filter((id) => selected.has(id)).length;
  const complete = heats.length > 0 && heats.every((heat) => perHeatCount(heat) === perHeat);

  const toggle = (heat: Heat, vehicleId: string) => {
    const next = new Set(selected);
    if (next.has(vehicleId)) {
      next.delete(vehicleId);
    } else {
      // Selecting past the limit replaces within *this heat* rather than refusing. Refusing means a tap
      // that does nothing, and a parent standing in a driveway reads that as the app being broken
      // rather than as a rule.
      if (perHeatCount(heat) >= perHeat) {
        const oldest = heat.participants.find((id) => next.has(id));
        if (oldest) next.delete(oldest);
      }
      next.add(vehicleId);
    }
    setDraft(next);
  };

  const submit = () => {
    if (!openRound) return;
    void run(
      () =>
        api.submitPick(teamId, tournament.tournamentId, {
          round: openRound,
          advancers: [...selected],
        }),
      () => {
        setDraft(null);
        onChanged();
      },
    );
  };

  return (
    <>
      {/* Requirement 9.3 — the champion pick is made before round one, so it appears only while round
          one is still open, and afterwards becomes a record of what was chosen. */}
      <div className="card">
        <p className="section-title">Champion</p>
        {championState === "open" ? (
          <>
            <p className="muted" style={{ marginBottom: 10 }}>
              {myChampionPick
                ? `You picked ${names.get(myChampionPick) ?? "—"}. Tap another to change it.`
                : "Who wins the whole thing? Worth a full round on its own."}
            </p>
            <div className="chips">
              {tournament.entrants.map((entrant) => (
                <button
                  key={entrant.vehicleId}
                  className={`chip${(champion ?? myChampionPick) === entrant.vehicleId ? " picked" : ""}`}
                  disabled={busy}
                  onClick={() => {
                    setChampion(entrant.vehicleId);
                    void run(
                      () =>
                        api.submitPick(teamId, tournament.tournamentId, {
                          round: CHAMPION_ROUND,
                          champion: entrant.vehicleId,
                        }),
                      onChanged,
                    );
                  }}
                >
                  {entrant.name ?? "—"}
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {myChampionPick
              ? `You picked ${names.get(myChampionPick) ?? "—"}.`
              : "Racing started before you picked a champion."}
          </p>
        )}
      </div>

      {openRound !== null && (
        <div className="card">
          <p className="section-title">
            {openRound === tournament.roundCount ? "The final" : `Round ${openRound}`} — who goes
            through?
          </p>
          <p className="muted" style={{ marginBottom: 10 }}>
            {perHeat === 1
              ? "Pick the winner of each race."
              : `Pick ${perHeat} from each race. Order doesn't matter.`}
          </p>

          {heats.map((heat) => (
            <div key={heat.index} style={{ marginBottom: 12 }}>
              <p className="muted" style={{ margin: "0 0 6px 2px" }}>
                Race {heat.index + 1}
              </p>
              {heat.participants.map((vehicleId) => (
                <button
                  key={vehicleId}
                  className={`lane${selected.has(vehicleId) ? " picked" : ""}`}
                  disabled={busy}
                  onClick={() => toggle(heat, vehicleId)}
                >
                  <span className="place">{selected.has(vehicleId) ? "✓" : ""}</span>
                  <span className="grow name">{names.get(vehicleId) ?? "—"}</span>
                  {/* Requirement 9.9 — who else backed this one, as avatars and never names. Empty until
                      the round reveals (9.4), because the server simply does not send other people's
                      unrevealed picks; there is no client-side rule here that could be got wrong. */}
                  <PickedBy
                    subs={backersOf(tournament, heat, vehicleId).filter((sub) => sub !== mySub)}
                    members={members}
                  />
                </button>
              ))}
            </div>
          ))}

          <button className="btn primary wide" disabled={busy || !complete} onClick={submit}>
            {complete
              ? myPicks.has(openRound)
                ? "Change my pick"
                : "Lock it in"
              : `Pick ${perHeat} from every race`}
          </button>
          {/* Requirement 9.5, said out loud. A family should know that not picking is allowed and
              costs only this round, rather than wondering whether they are holding everyone up. */}
          <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
            You can change this until the first race of the round is called. Nobody waits for you.
          </p>
        </div>
      )}

      {openRound === null && (
        <div className="banner info">
          {tournament.state === "complete"
            ? "All done — see the leaderboard below."
            : "Nothing to pick right now. The next round opens when this one finishes."}
        </div>
      )}

      <Presence
        tournament={tournament}
        members={members}
        round={openRound ?? CHAMPION_ROUND}
      />
    </>
  );
}

/**
 * Who has picked, as avatars. Requirements 9.5 and 9.9.
 *
 * Shows *that* somebody has picked, never *what* — the server does not send another member's unrevealed
 * pick at all, so this cannot leak it even by accident. Worth showing because requirement 9.5's
 * sit-it-out is only legible if you can see who is still missing: without this, "nobody waits for you"
 * reads as a threat rather than as a fact about how the game works.
 */
function Presence({
  tournament,
  members,
  round,
}: {
  tournament: Tournament;
  members: Member[];
  round: number;
}) {
  const picked = new Set(
    tournament.picks.filter((pick) => pick.round === round).map((pick) => pick.sub),
  );
  if (members.length <= 1) return null;

  return (
    <div className="card">
      <div className="row">
        <p className="section-title grow" style={{ margin: 0 }}>
          {picked.size} of {members.length} in
        </p>
        <div className="avatars">
          {members.map((member) => (
            <MemberAvatar
              key={member.sub}
              sub={member.sub}
              name={member.name}
              faded={!picked.has(member.sub)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
